import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, of, pipe, switchMap, tap} from 'rxjs';
import {AdminRestaurantService} from '../services/admin-restaurant.service';
import {extractErrorMessage} from '@core/utils/error-handler.util';

export interface Restaurant {
  id: number;
  name: string;
  description?: string;
  cuisineType?: string;
  priceRange?: string;
  phone?: string;
  email?: string;
  website?: string;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  imageUrl?: string;
  logoUrl?: string;
  city?: string;
  address?: any;
  rating: number;
  reviewsCount: number;
  ordersCount: number;
  menuItemsCount: number;
  revenue: number;
  createdAt: Date;
  updatedAt: Date;
}

interface AdminRestaurantState {
  restaurants: Restaurant[];
  selectedRestaurant: Restaurant | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalElements: number;
}

const initialState: AdminRestaurantState = {
  restaurants: [],
  selectedRestaurant: null,
  loading: false,
  saving: false,
  error: null,
  currentPage: 0,
  totalPages: 0,
  totalElements: 0
};

export const AdminRestaurantStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),

  withComputed((state) => ({
    totalRestaurants: computed(() => state.restaurants().length),

    activeRestaurants: computed(() =>
      state.restaurants().filter(r => r.status === 'active').length
    ),

    pendingRestaurants: computed(() =>
      state.restaurants().filter(r => r.status === 'pending').length
    ),

    totalRevenue: computed(() =>
      state.restaurants().reduce((sum, r) => sum + (r.revenue || 0), 0)
    )
  })),

  withMethods((store, restaurantService = inject(AdminRestaurantService)) => ({
    loadRestaurants: rxMethod<{ page?: number; sortBy?: string } | void>(
      pipe(
        tap(() => patchState(store, {loading: true, error: null})),
        switchMap((params) =>
          restaurantService.getRestaurants(params || {}).pipe(
            tap((response: any) => {
              patchState(store, {
                restaurants: response.content || response || getDemoRestaurants(),
                loading: false,
                currentPage: response.number || 0,
                totalPages: response.totalPages || 1,
                totalElements: response.totalElements || response.length || 0
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error),
                restaurants: getDemoRestaurants()
              });
              return of(null);
            })
          )
        )
      )
    ),

    loadRestaurantById: rxMethod<number>(
      pipe(
        tap(() => patchState(store, {loading: true})),
        switchMap((id) =>
          restaurantService.getRestaurantById(id).pipe(
            tap((restaurant) => {
              patchState(store, {
                selectedRestaurant: restaurant,
                loading: false
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error)
              });
              return of(null);
            })
          )
        )
      )
    ),

    createRestaurant: rxMethod<Partial<Restaurant>>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((restaurant) =>
          restaurantService.createRestaurant(restaurant).pipe(
            tap((newRestaurant) => {
              const restaurants = [...store.restaurants(), {
                ...restaurant,
                id: newRestaurant?.id || Date.now(),
                rating: 0,
                reviewsCount: 0,
                ordersCount: 0,
                menuItemsCount: 0,
                revenue: 0,
                createdAt: new Date(),
                updatedAt: new Date()
              } as Restaurant];
              patchState(store, {restaurants, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    updateRestaurant: rxMethod<Restaurant>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((restaurant) =>
          restaurantService.updateRestaurant(restaurant.id, restaurant).pipe(
            tap(() => {
              const restaurants = store.restaurants().map(r =>
                r.id === restaurant.id ? {...restaurant, updatedAt: new Date()} : r
              );
              patchState(store, {restaurants, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    updateRestaurantStatus: rxMethod<{ id: number; status: string }>(
      pipe(
        switchMap(({id, status}) =>
          restaurantService.updateRestaurantStatus(id, status).pipe(
            tap(() => {
              const restaurants = store.restaurants().map(r =>
                r.id === id ? {...r, status: status as any, updatedAt: new Date()} : r
              );
              patchState(store, {restaurants});
            }),
            catchError((error) => {
              patchState(store, {error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    deleteRestaurant: rxMethod<number>(
      pipe(
        switchMap((id) =>
          restaurantService.deleteRestaurant(id).pipe(
            tap(() => {
              const restaurants = store.restaurants().filter(r => r.id !== id);
              patchState(store, {restaurants});
            }),
            catchError((error) => {
              patchState(store, {error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    approveRestaurant: rxMethod<number>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((id) =>
          restaurantService.approveRestaurant(id).pipe(
            tap(() => {
              const restaurants = store.restaurants().map(r =>
                r.id === id ? {...r, status: 'active' as const, updatedAt: new Date()} : r
              );
              patchState(store, {restaurants, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    suspendRestaurant: rxMethod<{ id: number; reason: string }>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap(({id, reason}) =>
          restaurantService.suspendRestaurant(id, reason).pipe(
            tap(() => {
              const restaurants = store.restaurants().map(r =>
                r.id === id ? {...r, status: 'suspended' as const, updatedAt: new Date()} : r
              );
              patchState(store, {restaurants, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    clearError(): void {
      patchState(store, {error: null});
    },

    clearSelectedRestaurant(): void {
      patchState(store, {selectedRestaurant: null});
    }
  }))
);

function getDemoRestaurants(): Restaurant[] {
  return [
    {
      id: 1,
      name: 'Le Gourmet Parisien',
      description: 'Restaurant gastronomique français au cœur de Paris',
      cuisineType: 'Française',
      priceRange: '$$$',
      phone: '+33 1 42 86 82 82',
      email: 'contact@legourmet.fr',
      status: 'active',
      imageUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400',
      city: 'Paris',
      rating: 4.7,
      reviewsCount: 342,
      ordersCount: 1250,
      menuItemsCount: 45,
      revenue: 125000,
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date()
    },
    {
      id: 2,
      name: 'Pizza Napoli',
      description: 'Pizzeria authentique napolitaine avec four à bois',
      cuisineType: 'Italienne',
      priceRange: '$$',
      phone: '+33 1 45 67 89 00',
      email: 'info@pizzanapoli.fr',
      status: 'active',
      imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400',
      city: 'Lyon',
      rating: 4.5,
      reviewsCount: 567,
      ordersCount: 2340,
      menuItemsCount: 32,
      revenue: 89000,
      createdAt: new Date('2024-02-20'),
      updatedAt: new Date()
    },
    {
      id: 3,
      name: 'Sushi Master',
      description: 'Sushis frais préparés par des chefs japonais',
      cuisineType: 'Japonaise',
      priceRange: '$$$',
      phone: '+33 1 56 78 90 12',
      email: 'contact@sushimaster.fr',
      status: 'active',
      imageUrl: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=400',
      city: 'Paris',
      rating: 4.8,
      reviewsCount: 423,
      ordersCount: 1890,
      menuItemsCount: 58,
      revenue: 156000,
      createdAt: new Date('2024-03-10'),
      updatedAt: new Date()
    },
    {
      id: 4,
      name: 'Burger House',
      description: 'Les meilleurs burgers artisanaux de la ville',
      cuisineType: 'Américaine',
      priceRange: '$$',
      phone: '+33 1 67 89 01 23',
      email: 'hello@burgerhouse.fr',
      status: 'pending',
      imageUrl: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400',
      city: 'Marseille',
      rating: 4.3,
      reviewsCount: 189,
      ordersCount: 890,
      menuItemsCount: 24,
      revenue: 45000,
      createdAt: new Date('2024-06-01'),
      updatedAt: new Date()
    },
    {
      id: 5,
      name: 'Thai Express',
      description: 'Cuisine thaïlandaise authentique et épicée',
      cuisineType: 'Thaïlandaise',
      priceRange: '$$',
      phone: '+33 1 78 90 12 34',
      status: 'inactive',
      imageUrl: 'https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=400',
      city: 'Bordeaux',
      rating: 4.4,
      reviewsCount: 234,
      ordersCount: 567,
      menuItemsCount: 38,
      revenue: 34000,
      createdAt: new Date('2024-04-15'),
      updatedAt: new Date()
    }
  ];
}

